import { getViewStorage } from "./viewStorage.js";
import React from "react";
import { Buffer } from "buffer";
import { isArray } from "util";
import {
  submitSolrQuery,
  filterToString,
  FacetData,
  AllFacetData,
} from "./Query.js";
import {
  checkAcl,
  getMultiCampuslist,
  checkMultiCampus,
} from "../../adminressources/ACLHelper.js";
import { Field_Info, config as getConfig, T } from "./Config.js";

import { Tag, Tooltip, Whisper, toaster, Message } from "rsuite";
import Cookies from "universal-cookie";

export const cookies = new Cookies();
// Sets description limit
export const desc_limit = 1000;

// Used by bbox to check if arary set is equal
export const setsEqual = (a, b) =>
  a.length === b.size && a.every((value) => b.has(value));

// checks if value and input match
const matches = (value_match, input) => {
  let value = input.field !== undefined ? input.field.value : input;

  if (isArray(value)) {
    return value.some((val) => matches(value_match, val));
  } else {
    let val = value + "";

    if (value_match.value !== undefined && value_match.value !== null) {
      return val === value_match.value;
    }

    if (value_match.regex !== undefined && value_match.regex !== null) {
      let regex = new RegExp(value_match.regex.expression);

      return regex.test(val) === value_match.regex.requires_match;
    }

    return true;
  }
};

export const isImpersonationSession = () => sessionStorage.getItem("SB_IMPERSONATION_ACTIVE") === "1";

export const beginImpersonationSession = () => {
  const prefix = "#impersonate:";
  if (!window.location.hash.startsWith(prefix)) return;
  const token = window.location.hash.slice(prefix.length);
  sessionStorage.setItem("SB_IMPERSONATION_ACTIVE", "1");
  sessionStorage.removeItem("SB_IMPERSONATION_LOGIN");
  if (token) sessionStorage.setItem("SB_IMPERSONATION_TOKEN", token);
  else sessionStorage.removeItem("SB_IMPERSONATION_TOKEN");
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
};

export const storeImpersonationLogin = (data) => {
  sessionStorage.setItem("SB_IMPERSONATION_LOGIN", JSON.stringify(data));
  sessionStorage.setItem("SB_IMPERSONATION_TOKEN", data.ticket);
  sessionStorage.setItem("SB_IMPERSONATION_VALID", "Okay");
};

export const clearImpersonationLogin = () => {
  sessionStorage.removeItem("SB_IMPERSONATION_LOGIN");
  sessionStorage.removeItem("SB_IMPERSONATION_TOKEN");
  sessionStorage.removeItem("SB_IMPERSONATION_VALID");
  sessionStorage.removeItem("D");
  // Keep the marker so this tab never falls back to the administrator's login.
};

// Run before modules that cache getToken() at import time are evaluated.
beginImpersonationSession();

export const getStoredDriveSession = () => (
  isImpersonationSession() ? sessionStorage : localStorage
).getItem("D");

// Method called to get the token from the local storage
export const getToken = () => {
  if (isImpersonationSession()) return sessionStorage.getItem("SB_IMPERSONATION_TOKEN");
  let token = localStorage.getItem("T");
  if (token !== undefined && token !== null) {
    const decrypted = decrypt_storage_values(token);
    return decrypted;
  }
  return token;
};

// Method called to get the expiry from cookies
export const getExpires = () => {
  if (isImpersonationSession()) {
    const info = getJWTInfo(getToken());
    return info?.expires || "";
  }
  let expires = cookies.get("E");
  if (expires !== undefined && expires !== null) {
    return JSON.parse(decrypt_storage_values(expires));
  }
  return "";
};

// JWT Token Utilities
// Checks if a token is in JWT format (three base64url-encoded parts separated by dots)
export const isJWT = (token) => {
  if (!token || typeof token !== "string") return false;
  const parts = token.split(".");
  return parts.length === 3 && parts.every((part) => part.length > 0);
};

// Returns the token authentication type for logging and diagnostics
export const getTokenType = (token) => {
  if (!token) return "none";
  return isJWT(token) ? "jwt" : "aes";
};

// Extracts basic info from a JWT token without verification (client-side read only)
// Returns null for non-JWT tokens. Does NOT validate the signature.
export const getJWTInfo = (token) => {
  if (!isJWT(token)) return null;
  try {
    const payload = token.split(".")[1];
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    );
    return {
      username: decoded.sub || null,
      issued: decoded.iat ? new Date(decoded.iat * 1000) : null,
      expires: decoded.exp ? new Date(decoded.exp * 1000) : null,
      tokenId: decoded.jti || null,
      source: decoded.source || null,
    };
  } catch (e) {
    return null;
  }
};

// Checks if a JWT token is expired based on its exp claim (client-side only).
// Returns true if expired, false if still valid or not a JWT.
// Includes a small grace period (30s) to account for clock drift.
export const isJWTExpired = (token) => {
  const info = getJWTInfo(token);
  if (!info || !info.expires) return false; // Not a JWT or no exp claim — not "expired"
  const gracePeriodMs = 30 * 1000; // 30 seconds
  return new Date() >= new Date(info.expires.getTime() - gracePeriodMs);
};

// Validates token integrity — checks that a JWT token can be decoded without errors.
// Returns { valid: true, info } on success, or { valid: false, reason } on failure.
// For non-JWT (AES) tokens, returns { valid: true, type: "aes" } since we can't validate those client-side.
export const validateTokenIntegrity = (token) => {
  if (!token || typeof token !== "string") {
    return { valid: false, reason: "empty" };
  }
  if (!isJWT(token)) {
    return { valid: true, type: "aes" }; // AES tokens — trust the server to validate
  }
  const info = getJWTInfo(token);
  if (!info) {
    return { valid: false, reason: "corrupt", type: "jwt" };
  }
  if (!info.username) {
    return { valid: false, reason: "missing-sub", type: "jwt" };
  }
  return { valid: true, type: "jwt", info };
};

// Method called to get the view from the session storage
export const getView = () => {
  let view = JSON.parse(getViewStorage().getItem("V"));
  return view;
};

// Method to get the login details
export const getLogin = () => {
  if (isImpersonationSession()) {
    const login = sessionStorage.getItem("SB_IMPERSONATION_LOGIN");
    return login ? JSON.parse(login) : null;
  }
  let login = localStorage.getItem("L");
  if (login !== undefined && login !== null) {
    return JSON.parse(decrypt_storage_values(login));
  }
  return login;
};

// Method to get the login details
export const updateConfig = (new_config) => {
  if (isImpersonationSession()) {
    const login = getLogin();
    if (login) {
      new_config.splice(0, 1);
      login.saved_configs = new_config;
      sessionStorage.setItem("SB_IMPERSONATION_LOGIN", JSON.stringify(login));
    }
    return;
  }
  new_config.splice(0, 1);
  let login = localStorage.getItem("L");
  if (login !== undefined && login !== null) {
    let current_login = JSON.parse(decrypt_storage_values(login));
    current_login.saved_configs = new_config;
    localStorage.setItem(
      "L",
      encrypt_storage_values(JSON.stringify(current_login)),
    );
  }
};

export const getLoginValid = () => {
  let login = getLogin();
  return login.is_active;
};

export const checkReindex = async (type) => {
  let token = getToken();
  if (type === "customise") {
    token = await T();
  }
  // Api call to return the dataimpoart check.
  return await fetch("../admin/api/v1/solr/dataimport", {
    method: "GET",
    headers: { "Semabench-Token": token },
  })
    .then((res) => {
      return res.json();
    })
    .then((response) => {
      if (
        response.status === "busy" &&
        response.statusMessages["Total Rows Fetched"] !== undefined &&
        response.statusMessages["Time Elapsed"] !== undefined &&
        response.statusMessages["Full Dump Started"] !== undefined
      ) {
        toaster.push(
          <Message type="info" showIcon closable>
            SchoolBench is currently running a reindex process. Your requested
            change cannot be made until this process is completed.
          </Message>,
          { duration: 10000 },
        );
        return true;
      } else {
        return false;
      }
    });
};

export const conditionMatches = (create_mode, conditions, fields) => {
  if (conditions.create_mode !== undefined && conditions.create_mode !== null) {
    if (create_mode !== conditions.create_mode) {
      return false;
    }
  }

  if (conditions.edit_mode !== undefined && conditions.edit_mode !== null) {
    if (create_mode === conditions.edit_mode) {
      return false;
    }
  }

  let change_fields = Object.keys(fields).filter((field) => {
    let val = fields[field];

    if (val !== undefined && val !== null) {
      if (val.field !== undefined && val.field !== null) {
        val = val.field.value;
      }
      if (isArray(val)) {
        return val.some((val) => (val + "").length > 0);
      } else {
        return (val + "").length > 0;
      }
    }

    return false;
  });

  if (
    conditions.required_fields !== undefined &&
    conditions.required_fields !== null
  ) {
    let has_missing_fields = conditions.required_fields.some(
      (field) => change_fields.indexOf(field) === -1,
    );

    if (has_missing_fields) {
      return false;
    }
  }

  if (
    conditions.absent_fields !== undefined &&
    conditions.absent_fields !== null
  ) {
    let has_fields = conditions.absent_fields.some(
      (field) => change_fields.indexOf(field) !== -1,
    );

    if (has_fields) {
      return false;
    }
  }

  if (
    conditions.matched_values !== undefined &&
    conditions.matched_values !== null
  ) {
    let matched_values = conditions.matched_values;

    let has_different_value = Object.keys(matched_values).some((field) => {
      if (fields[field] === undefined || fields[field] === null) {
        return true;
      }

      let val = fields[field];

      return !matches(matched_values[field], val);
    });

    if (has_different_value) {
      return false;
    }
  }

  if (
    conditions.unmatched_values !== undefined &&
    conditions.unmatched_values !== null
  ) {
    let unmatched_values = conditions.unmatched_values;
    let has_same_value = Object.keys(unmatched_values).some((field) => {
      if (fields[field] !== undefined && fields[field] !== null) {
        let val = fields[field];

        return matches(unmatched_values[field], val);
      }

      return false;
    });

    if (has_same_value) {
      return false;
    }
  }

  return true;
};

const getPathNodes = (isActive, query, config) => {
  if (query.facets["path_starts_with"]) {
    let raw_paths = query.facets["path_starts_with"];
    let path_keys = Object.keys(raw_paths);

    // used to clean any unwanted characters that might be placed.
    const cleanupPath = (path) => {
      let output_path = path.replace(config.path_strip, "").split("/");
      return output_path;
    };

    var output = [];
    for (var i = 0; i < path_keys.length; i++) {
      if (path_keys[i].startsWith(config.path_strip)) {
        var chain = cleanupPath(path_keys[i]);
        var current_node = output;

        for (var j = 0; j < chain.length; j++) {
          var wanted_node = chain[j];

          var last_node = current_node;

          for (var k = 0; k < current_node.length; k++) {
            if (current_node[k].label === wanted_node) {
              current_node = current_node[k].childNodes;
              break;
            }
          }

          // If we couldn't find an item in this list of children
          // that has the right name, create one:
          if (last_node === current_node) {
            var lastChain = j === chain.length - 1;

            let id = "path_" + chain.slice(0, j + 1).join("/");
            let value = config.path_strip + chain.slice(0, j + 1).join("/");
            let secondaryLabel = <span />;
            let isSelected = false;

            if (
              isActive &&
              config.view.filters.findIndex(
                (filter) =>
                  filter.field_name === "path_start_with" &&
                  filter.value.some((val) => val === value),
              )
            ) {
              isSelected = true;
            }

            if (lastChain && raw_paths[path_keys[i]]) {
              secondaryLabel = (
                <span>
                  {isSelected
                    ? raw_paths[path_keys[j]]
                    : raw_paths[path_keys[i]]}
                </span>
              );
            }

            var new_node = (current_node[k] = {
              id: id,
              type: "value",
              inverse: false,
              field_name: "path_starts_with",
              icon: "",
              label: wanted_node,
              secondaryLabel: secondaryLabel,
              isExpanded: false,
              value: value,
              isSelected: isSelected,
              childNodes: [],
            });

            current_node = new_node.childNodes;
          }
        }

        const removeCaret = (node) => {
          if (node.childNodes && node.childNodes.length > 0) {
            node.hasCaret = true;
            node.childNodes.forEach((node) => removeCaret(node));
          } else {
            node.icon =
              node.icon === "folder-open" ? "folder-close" : node.icon;
            node.hasCaret = false;
          }
        };
        output.forEach((node) => removeCaret(node));
        return output;
      } else {
        return [];
      }
    }
  }
};

export const getAlbumUid = (node) => {
  let token = getToken();
  let header = {
    "Semabench-Token": token,
    "Content-Type": "application/json",
  };

  return fetch("../api/v1/album/user?node=" + node, {
    headers: header,
  })
    .then((resp) => resp.json())
    .then((result) => {
      return result;
    });
};

export const clearFilter = async () => {
  let view = getView();
  view.filters = [];
  getViewStorage().setItem("V", JSON.stringify(view));
  await submitSolrQuery("clean-query").then(() => {
    return;
  });
};

export const buildFacetNode = (
  facet,
  { view = getView(), query = FacetData, info = Field_Info } = {},
) => {
  if (
    facet === undefined ||
    facet === null ||
    query === undefined ||
    query === null
  ) {
    return null;
  }

  const facetResponse = query[facet];

  if (facetResponse === undefined || facetResponse === null) {
    return null;
  }

  const filters = Array.isArray(view?.filters) ? view.filters : [];
  const filterRules = view?.filter_rules || [];
  const isActive = filters.some((filter) => filter.field_name === facet);
  const entries = Array.isArray(facetResponse) ? facetResponse : [];
  let children = [];

  if (facet === "path_starts_with") {
    children = getPathNodes(isActive); //TODO need to check this
  } else {
    let sorted_terms = entries.map((bucket) => bucket.val);
    let is_list = false;

    if (info.fields[facet] !== undefined && info.fields[facet] !== null) {
      let field_info = info.fields[facet];

      if (field_info.constraints != null) {
        let constraints = info.fields[facet].constraints;
        if (
          constraints !== undefined &&
          constraints.list !== undefined &&
          constraints !== null &&
          constraints.list !== null
        ) {
          sorted_terms = constraints.list;
          is_list = true;
        }
      }
    }

    if (isActive) {
      filters
        .filter((filter) => filter.field_name === facet)
        .forEach((filter) => {
          filter.value.forEach((val) => {
            if (sorted_terms.indexOf(val) === -1) {
              sorted_terms.push(val);
            }
          });
        });
    }

    let facet_query = false;
    if (facet_query === true) {
    } else if (sorted_terms !== null && sorted_terms !== undefined) {
      const entriesByValue = new Map(
        entries.map((bucket) => [bucket.val, bucket]),
      );

      children = sorted_terms.map((child, childIndex) => {
        let bucket = entriesByValue.get(child);
        let count = bucket !== undefined ? bucket.count : 0;

        let secondaryLabel =
          bucket !== undefined || is_list ? <span>{count} </span> : undefined;
        let isSelected = false;
        let inverse = false;
        let filter_rule = undefined;

        let filter = undefined;
        let active_filter_rule = filterRules;

        let rule = Object.entries(filterRules)
          .filter((val) => active_filter_rule.some((rule) => rule === val[0]))
          .find(
            (val) =>
              val[1].field_name === facet &&
              val[1].value.some((val) => val === child),
          );

        if (rule !== undefined && rule !== null) {
          filter_rule = rule[0];
          filter = rule[1];
        }

        if (filter === undefined || filter === null) {
          filter = filters.find(
            (filter) =>
              filter.field_name === facet &&
              filter.value.some((val) => val === child),
          );
        }

        if (filter !== undefined && filter !== null) {
          inverse = filter.inverse;
          isSelected = true;
        }

        let label_value = info.lookup[child] || child;
        let label;
        let tooltipAddToAlbum = <Tooltip>{label_value}</Tooltip>;

        if (
          facet === "sb_event" ||
          facet === "sb_tags" ||
          facet === "sb_class" ||
          facet === "sb_student" ||
          facet === "sb_staff" ||
          facet === "sb_visitor" ||
          facet === "sb_pastStudent"
        ) {
          label = (
            <Whisper
              placement="bottom"
              trigger="hover"
              speaker={tooltipAddToAlbum}
            >
              <span className="d-flex flex-row wbr200">
                <span>{label_value}</span>
                <Tag
                  className="float-right text-xxsmall mt-0 ml-2 pt-0 pb-0"
                  color="blue"
                >
                  {count}
                </Tag>
              </span>
            </Whisper>
          );
        } else {
          label = (
            <span className="d-flex flex-row wbr200">
              <span>{label_value}</span>
              <Tag
                className="float-right text-xxsmall mt-0 ml-2 pt-0 pb-0"
                color="blue"
              >
                {count}
              </Tag>
            </span>
          );
        }

        if (inverse) {
          if (
            facet === "sb_event" ||
            facet === "sb_tags" ||
            facet === "sb_class" ||
            facet === "sb_student" ||
            facet === "sb_staff" ||
            facet === "sb_visitor" ||
            facet === "sb_pastStudent"
          ) {
            label = (
              <Whisper
                placement="bottom"
                trigger="hover"
                speaker={tooltipAddToAlbum}
              >
                <span className="d-flex flex-row wbr200">
                  <span title={label_value}>- {label_value}</span>
                  <Tag
                    className="float-right text-xxsmall mt-0 ml-2 pt-0 pb-0"
                    color="blue"
                  >
                    {count}
                  </Tag>
                </span>
              </Whisper>
            );
          } else {
            label = (
              <span className="d-flex flex-row wbr200">
                <span>- {label_value}</span>
                <Tag
                  className="float-right text-xxsmall mt-0 ml-2 pt-0 pb-0"
                  color="blue"
                >
                  {count}
                </Tag>
              </span>
            );
          }
        }

        let readableName = info.lookup[facet] || facet;

        return {
          id: facet + childIndex,
          type: "value",
          inverse,
          labelExtra: label,
          label: label_value,
          filter_rule,
          className: "",
          icon: "",
          secondaryLabel: secondaryLabel,
          isSelected: isSelected,
          field_name: facet,
          value: child,
          readableName,
        };
      });
    }
  }

  let isExpanded = false;
  let isSelected = isExpanded === false ? isActive : false;

  return {
    id: facet,
    hasCaret: true,
    isExpanded: isExpanded,
    isSelected: isSelected,
    label: facet,
    field_name: facet,
    icon: "",
    childNodes: children,
  };
};

export const getNodes = (addedNode) => {
  let view = getView();
  let facets = Array.isArray(view?.facets) ? [...view.facets] : [];

  if (addedNode !== undefined && addedNode !== null) {
    facets.push(addedNode);
  }

  let query = FacetData;
  if (query === undefined || query === null) {
    query = [];
  }

  if (query === undefined || query === null) {
    return [];
  }

  let facet_response = query;
  if (facet_response === undefined || facet_response === null) {
    return [];
  }

  let info = Field_Info;

  return facets
    .filter(
      (facet) =>
        facet_response[facet] !== null && facet_response[facet] !== undefined,
    )
    .map((facet) => buildFacetNode(facet, { view, query, info }))
    .filter((node) => node !== null);
};

// Gets the src of the image
// helper.js
export const getSrc = (props, type) => {
  // Auth + cache-busting
  let token = getToken();
  let token_encode = encodeURIComponent(token ?? "");
  const solrVersion = String(props?.cm_modified ?? "0");

  // Normalize preview kinds to a lowercase array
  const toKinds = (pk) => {
    if (!pk) return [];
    if (Array.isArray(pk)) return pk.map((x) => String(x).toLowerCase());
    if (typeof pk === "string") {
      return pk
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
    }
    return [String(pk).toLowerCase()];
  };
  const kinds = toKinds(props?.preview_kind);

  // Safe name + mime helpers (guard against arrays)
  const mime = String(props?.mime_type ?? "").toLowerCase();
  const nameLower = (() => {
    const n = Array.isArray(props?.cm_name) ? props.cm_name[0] : props?.cm_name;
    return String(n ?? "").toLowerCase();
  })();

  // Media type booleans
  const isVideo =
    mime.startsWith("video/") ||
    /\.(mp4|mov|m4v|webm|ogv|ogg)$/i.test(nameLower);

  const isPdf = mime === "application/pdf" || /\.pdf$/i.test(nameLower);

  const isHeif =
    mime.includes("heic") ||
    mime.includes("heif") ||
    /\.(heic|heif)$/i.test(nameLower);

  const rawMimeHints = [
    "x-adobe-dng",
    "x-canon-cr2",
    "x-canon-cr3",
    "x-sony-arw",
    "x-nikon-nef",
    "x-raw-fuji",
    "x-raw-nikon",
    "image/arw",
    "image/dng",
    "image/nef",
  ];
  const isRawMime = rawMimeHints.some((hint) => mime.includes(hint));

  const isRaw =
    /\.(cr2|cr3|nef|arw|dng|rw2|orf|raf|srw|pef)$/i.test(nameLower) ||
    mime.includes("x-raw") ||
    mime.includes("image/raw") ||
    isRawMime;

  // Utility finders over preview kinds
  const findExact = (needle) => kinds.find((k) => k === needle);
  const findContains = (needle) => kinds.find((k) => k.includes(needle));

  // Handle specific type requests
  if (type && type !== "betterthumb") {
    // Special handling for "original":
    // - Videos: return original for playback (video players need the stream)
    // - HEIC/RAW/PDF: fall through to get converted preview (browsers can't display original)
    // - Regular images: return original (browsers can display)
    if (type === "original") {
      if (isVideo) {
        // Videos: return original stream for video player
        const kind = "original";
        return `../api/v1/repo/s/semabench/preview?id=${props.id}&semabench-token=${token_encode}&kind=${kind}&version=${solrVersion}`;
      } else if (isPdf || isHeif || isRaw) {
        // HEIC/RAW/PDF: fall through to auto-selection to get converted preview
        // (don't return here, continue to auto-selection below)
      } else {
        // Regular images: return original
        const kind = "original";
        return `../api/v1/repo/s/semabench/preview?id=${props.id}&semabench-token=${token_encode}&kind=${kind}&version=${solrVersion}`;
      }
    } else {
      // Any other specific type request (doclib, xthumb, etc.)
      const kind = type;
      return `../api/v1/repo/s/semabench/preview?id=${props.id}&semabench-token=${token_encode}&kind=${kind}&version=${solrVersion}`;
    }
  }

  // Auto-selection for thumbnails / preview when no type specified
  // OR when type="original" was requested for HEIC/RAW/PDF (fell through above)
  let kind = null;

  if (isVideo) {
    // For video THUMBNAILS (gallery/list view), use static image previews
    kind =
      findExact("xthumb") || // ideal, static image for videos
      findContains("thumb") || // any "*thumb*"
      findContains("bbox") || // rasterized preview
      findExact("doclib") || // doclib can be video stream OR thumbnail
      "xthumb"; // fallback: force xthumb
  } else if (isPdf) {
    // PDFs need a raster preview
    kind =
      findExact("pdfthumb") ||
      findContains("thumb") ||
      findExact("doclib") ||
      "original";
  } else if (isHeif) {
    // HEIC/HEIF need a rasterized preview
    kind =
      findExact("heifbbox") ||
      findExact("doclib") ||
      findContains("thumb") ||
      findContains("bbox") ||
      "original";
  } else if (isRaw) {
    // RAW need a rasterized preview
    kind =
      findExact("tiffbbox") ||
      findExact("rawbbox") ||
      findExact("heifbbox") ||
      findExact("doclib") ||
      findExact("imgpreview") ||
      findContains("thumb") ||
      findContains("bbox") ||
      findContains("preview") ||
      findContains("medium") ||
      "original";
  } else {
    // Standard images/others: prefer doclib -> any thumb -> bbox -> original
    kind =
      findExact("doclib") ||
      findContains("thumb") ||
      findContains("bbox") ||
      "original";
  }

  return `../api/v1/repo/s/semabench/preview?id=${props.id}&semabench-token=${token_encode}&kind=${kind}&version=${solrVersion}`;
};

// const algorithm = "aes-256-cbc";

// generate 16 bytes of random data
const initVector = Buffer("initVector comon");

// secret key generate 32 bytes of random data
const Securitykey = "This needs to be a long password";

export const encrypt = (value) => {
  const crypto = require("crypto-js");

  let ciphertext = crypto.AES.encrypt(
    value,
    crypto.enc.Utf8.parse(Securitykey),
    {
      iv: crypto.enc.Utf8.parse(initVector), // parse the IV
      padding: crypto.pad.Pkcs7,
      mode: crypto.mode.CBC,
    },
  ).toString();

  return ciphertext;
};

export const decrypt = (value) => {
  const crypto = require("crypto-js");

  let cipher = crypto.AES.decrypt(value, crypto.enc.Utf8.parse(Securitykey), {
    iv: crypto.enc.Utf8.parse(initVector), // parse the IV ,
    padding: crypto.pad.Pkcs7,
    mode: crypto.mode.CBC,
  });

  return cipher.toString(crypto.enc.Utf8);
};

export const loadProfiles = async (val) => {
  // Gets the users local storage token
  let token = getToken();
  let config = getConfig;
  let view = getView();
  let headers = {
    "Content-Type": "application/json",
    "Semabench-Token": token,
    "Cache-Control": "max-age=0",
  };

  // Checks if a value has been parsed, if not its a blank string
  if (val === undefined || val === null || val === "") {
    val = "*:*";
  }

  // Gets the default filters from the config settings
  let filters = [config.default_filter];

  // Loads the info (facet) from local storeage
  let info = Field_Info;

  // Adds filters, as we are only looking for profiles
  view.filters = [
    { field_name: "sb_adminStatus", value: ["Profile"], inverse: false },
    { field_name: "sb_profileName", value: ["*"], inverse: true },
    { field_name: "sb_smsID", value: ["*"], inverse: true },
  ];

  // Add campus filter if multi campus is enabled and user is not admin
  if (checkMultiCampus() && !checkAcl(getLogin().roles, config.acls, "ADMIN")) {
    view.filters.push({
      field_name: "sb_studentCampus",
      value: getMultiCampuslist(),
      inverse: false,
    });
  }

  // Adds the custom filters into the filters variable
  filters = filters.concat(
    view.filters
      .filter((filter) => filter.field_name !== "Advanced")
      .map((filter) => filterToString(filter, info.fields)),
  );

  // Creates the json for the filters to send in the body of the fetch command
  let body = JSON.stringify({
    filter: filters,
  });

  // Sets the names that we will be searching on for the suggestive search
  let qf = `sb_profileName_q^2 sb_otherNames_q`;

  // Calls the query function to get the results
  return await fetch(
    `../api/v1/repo/s/semabench/query?q=${val.toLowerCase()}&wt=json&defType=edismax&qf=${qf}&pf=${qf}&rows=1000`,
    {
      method: "POST",
      headers: headers,
      body: body,
    },
  )
    .then((resp) => resp.json())
    .then((response) => {
      // If there is a response we step into
      if (response !== undefined && response !== null) {
        let result = response.response;
        let profiles = [];

        // Pushes "Unknown" into the filter if nothing is applied
        profiles.push({
          label: "Unknown",
          value: "",
          type: undefined,
          othername: "Unknown Uknown",
        });
        if (result !== undefined && result !== null) {
          // If there are docs from the response we step into
          if (result.docs !== undefined && result.docs !== null) {
            // For each response we create the drop down element
            result.docs.forEach((doc) => {
              let data = {
                label: doc.sb_profileName,
                value: doc.id,
                type: doc.sb_profileType,
              };
              profiles.push(data);
              // Adds an extra placeholder object in the drop down for other name
              if (
                doc.sb_otherNames !== undefined &&
                doc.sb_otherNames !== null
              ) {
                let data = {
                  label: doc.sb_otherNames[0] + " (Other name)",
                  value: doc.id,
                  type: doc.sb_profileType,
                };
                profiles.push(data);
              }
            });
          }
        }
        // Sets the list of profiles
        return profiles;
      }
    });
};

export const loadProfilesChange = async (val) => {
  // Gets the users local storage token
  let token = getToken();
  let config = getConfig;
  let view = getView();
  let headers = {
    "Content-Type": "application/json",
    "Semabench-Token": token,
    "Cache-Control": "max-age=0",
  };

  // Checks if a value has been parsed, if not its a blank string
  if (val === undefined || val === null || val === "") {
    val = "*:*";
  }

  // Gets the default filters from the config settings
  let filters = [config.default_filter];

  // Loads the info (facet) from local storeage
  let info = Field_Info;

  // Adds filters, as we are only looking for profiles
  view.filters = [
    { field_name: "sb_adminStatus", value: ["Profile"], inverse: false },
    { field_name: "sb_profileName", value: ["*"], inverse: true },
    { field_name: "sb_smsID", value: ["*"], inverse: true },
  ];

  // Add campus filter if multi campus is enabled and user is not admin
  if (checkMultiCampus() && !checkAcl(getLogin().roles, config.acls, "ADMIN")) {
    view.filters.push({
      field_name: "sb_studentCampus",
      value: getMultiCampuslist(),
      inverse: false,
    });
  }

  // Adds the custom filters into the filters variable
  filters = filters.concat(
    view.filters
      .filter((filter) => filter.field_name !== "Advanced")
      .map((filter) => filterToString(filter, info.fields)),
  );

  // Creates the json for the filters to send in the body of the fetch command
  let body = JSON.stringify({
    filter: filters,
  });

  // Sets the names that we will be searching on for the suggestive search
  let qf = `sb_profileName_q^2 sb_otherNames_q`;

  // Calls the query function to get the results
  return await fetch(
    `../api/v1/repo/s/semabench/query?q=${val.toLowerCase()}&wt=json&defType=edismax&qf=${qf}&pf=${qf}&rows=1000`,
    {
      method: "POST",
      headers: headers,
      body: body,
    },
  )
    .then((resp) => resp.json())
    .then((response) => {
      // If there is a response we step into
      if (response !== undefined && response !== null) {
        let result = response.response;
        let profiles = [];

        // Pushes "Unknown" into the filter if nothing is applied
        profiles.push({
          label: "Unknown",
          value: "",
          type: undefined,
          othername: "Unknown Uknown",
        });
        if (result !== undefined && result !== null) {
          // If there are docs from the response we step into
          if (result.docs !== undefined && result.docs !== null) {
            // For each response we create the drop down element
            result.docs.forEach((doc) => {
              const profileType = Array.isArray(doc.sb_profileType)
                ? doc.sb_profileType[0]
                : doc.sb_profileType;

              profiles.push({
                label: doc.sb_profileName,
                value: doc.id,
                type: profileType,
                yearLevel: Array.isArray(doc.sb_studentYearLevel)
                  ? doc.sb_studentYearLevel[0]
                  : doc.sb_studentYearLevel,
              });
              if (doc.sb_otherNames) {
                profiles.push({
                  label: `${doc.sb_otherNames[0]} (Other name)`,
                  value: doc.id,
                  type: profileType,
                  yearLevel: Array.isArray(doc.sb_studentYearLevel)
                    ? doc.sb_studentYearLevel[0]
                    : doc.sb_studentYearLevel,
                });
              }
            });
          }
        }
        // Sets the list of profiles
        return profiles;
      }
    });
};

// Method to check if items already encrypted
export const checkDecrypt = (value) => {
  let check_decryption = true;

  try {
    decrypt(value);
  } catch {
    check_decryption = false;
  }

  return check_decryption;
};

//encrypt the values on storages/cookies
export const encrypt_storage_values = (value) => {
  if (value !== undefined && value !== null) {
    const crypto = require("crypto-js");

    let ciphertext = crypto.AES.encrypt(
      value,
      crypto.enc.Utf8.parse(Securitykey),
      {
        iv: crypto.enc.Utf8.parse(initVector), // parse the IV
        padding: crypto.pad.Pkcs7,
        mode: crypto.mode.CBC,
      },
    ).toString();

    return ciphertext;
  } else {
    return null;
  }
};

//decrypt the values on storages/cookies
export const decrypt_storage_values = (value) => {
  if (value !== undefined && value !== null) {
    const crypto = require("crypto-js");

    let cipher = crypto.AES.decrypt(value, crypto.enc.Utf8.parse(Securitykey), {
      iv: crypto.enc.Utf8.parse(initVector), // parse the IV ,
      padding: crypto.pad.Pkcs7,
      mode: crypto.mode.CBC,
    });

    return cipher.toString(crypto.enc.Utf8);
  } else {
    return null;
  }
};

const SESSION_EXPIRED_KEY = "SB_SESSION_EXPIRED";
const SESSION_REPLACED_KEY = "SB_SESSION_REPLACED";
const SESSION_EXPIRY_TOAST_DURATION = 3000;
const SESSION_EXPIRY_REDIRECT_DELAY = 900;

let toastShown = false;
let sessionRecoveryInProgress = false;
let loginRedirectScheduled = false;

const showSessionExpiryToast = (message) => {
  if (toastShown) {
    return;
  }

  toastShown = true;
  toaster.push(
    <Message type="warning" showIcon closable>
      {message}
    </Message>,
    { duration: SESSION_EXPIRY_TOAST_DURATION },
  );
};

const preserveSessionFlag = (key) => {
  try {
    sessionStorage.setItem(key, "1");
  } catch (e) {
    // ignore sessionStorage failures
  }
};

const clearSessionStorage = ({ preserveReason = null } = {}) => {
  try {
    sessionStorage.clear();
    if (preserveReason === "replaced") {
      preserveSessionFlag(SESSION_REPLACED_KEY);
    } else if (preserveReason === "expired") {
      preserveSessionFlag(SESSION_EXPIRED_KEY);
    }
  } catch (e) {
    // ignore sessionStorage failures
  }
};

const handleExpiredSession = async (expires) => {
  if (sessionRecoveryInProgress) {
    return;
  }

  sessionRecoveryInProgress = true;
  const expiryDate = expires ? new Date(expires) : null;
  const sessionWasReplaced =
    expiryDate instanceof Date &&
    !Number.isNaN(expiryDate.getTime()) &&
    new Date() < expiryDate;

  redirect_to_login({ reason: sessionWasReplaced ? "replaced" : "expired" });
};

// Validate the current login token against the backend
export const Check_login_ticket = async () => {
  let token = getToken();
  let expires = getExpires();
  const tokenType =
    token && typeof token === "string" && token.split(".").length === 3
      ? "JWT"
      : "AES";

  // Client-side JWT expiry pre-check — skip server call if we already know it's expired (#2)
  if (tokenType === "JWT" && isJWTExpired(token)) {
    console.debug(
      "[Auth] JWT expired (client-side pre-check). Skipping server call.",
    );
    await redirect_to_login({
      reason: new Date() < new Date(expires) ? "replaced" : "expired",
    });
    return;
  }

  await fetch("../api/v1/repo/s/semabench/ticket", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Semabench-Token": token,
      source: "media",
    },
  }).then(async (res) => {
    if (res.status === 401) {
      await handleExpiredSession(expires);
      return;
    }

    return;
  });
};

//check the current login token on the admin pages
export const Check_login_ticket_admin = async (_page, _status) => {
  let token = getToken();
  let expires = getExpires();

  // Client-side JWT expiry pre-check — skip server call if we already know it's expired (#2)
  const tokenType = isJWT(token) ? "JWT" : "AES";
  if (tokenType === "JWT" && isJWTExpired(token)) {
    console.debug(
      "[Auth] JWT expired (client-side pre-check on admin page). Skipping server call.",
    );
    await redirect_to_login({
      reason: new Date() < new Date(expires) ? "replaced" : "expired",
    });
    return;
  }

  await fetch("../api/v1/repo/s/semabench/ticket", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Semabench-Token": token,
      source: "media",
    },
  }).then(async (res) => {
    if (res.status === 401) {
      await handleExpiredSession(expires);
      return;
    }

    return;
  });
};
/// REMOVED FROM DEV-MASTER
// //regenerate the login token and cookies
// const refresh_token = async () => {
//   let login = getLogin();
//   let expires = getExpires();
//   let config = getConfig;

//   if (!toastShown) {
//     toastShown = true;
//     toaster.push(
//       <Message type="warning" showIcon closable>
//         <strong>Your session has expired.</strong>
//         <br />
//         You will need to log back in again. This will occur automatically with SSO.
//       </Message>,
//       { duration: 5000 }
//     );
//   }

//   setTimeout(async () => {
//     localStorage.removeItem("L");
//     localStorage.removeItem("T");
//     localStorage.removeItem("D");
//     sessionStorage.clear()
//     cookies.remove("T", { path: "/" });
//     cookies.remove("E", { path: "/" });

//     await fetch("../api/v1/repo/s/semabench/new_ticket", {
//       method: "POST",
//       mode: 'no-cors',
//       headers: {
//         "Content-Type": "application/json",
//         "Accept": "application/json",
//         "Source": 'media'
//       },
//       body: JSON.stringify({ name: login.username, value: "", source: 'media' })
//     }).then(res => res.json())
//       .then(async data => {
//         if (data !== undefined && data !== null) {

//           localStorage.setItem("L", encrypt_storage_values(JSON.stringify(data)));
//           localStorage.setItem("T", encrypt_storage_values(data.ticket));
//           cookies.set('T', encrypt_storage_values(data.ticket), { path: '/', expires: new Date(expires), secure: true, sameSite: "Strict" });
//           cookies.set('E', encrypt_storage_values(JSON.stringify(new Date(expires))), { path: '/', expires: new Date(expires), secure: true, sameSite: "Strict" });

//           if (checkAcl(data.roles, config.acls, "VIEW_PROPERTIES") || checkAcl(data.roles, config.acls, "ADMIN")) {
//             window.location.replace(document.URL);
//             if ('caches' in window) {
//               caches.keys().then(function (names) {
//                 for (let name of names) caches.delete(name);
//               });
//             }
//             window.location.reload(true);
//           }
//         } else {
//           toaster.push(
//             <Message type="error" showIcon closable>You do not have View Permissions Now, please contact your IT support</Message>
//           );
//           // Maybe show alert as to no view permissions?
//         }
//         return data;
//       })
//   }, 5000)

// }

//redirect to the login page as the current login token is too old, and can not regenerate it.
const redirect_to_login = async ({ reason = "expired" } = {}) => {
  if (loginRedirectScheduled) {
    return;
  }

  loginRedirectScheduled = true;
  const isSessionReplaced = reason === "replaced";
  showSessionExpiryToast(
    isSessionReplaced ? (
      <>
        <strong>Your session is no longer active.</strong>
        <br />
        This account was opened in another browser. You are now being redirected
        to log in.
      </>
    ) : (
      <>
        <strong>Your session has expired.</strong>
        <br />
        You are now being redirected to log in.
      </>
    ),
  );

  window.setTimeout(() => {
    if (isImpersonationSession()) {
      clearImpersonationLogin();
      window.location.replace("/?session_expired=1");
      return;
    }
    cookies.remove("T", { path: "/" });
    cookies.remove("E", { path: "/" });
    clearSessionStorage({
      preserveReason: isSessionReplaced ? "replaced" : "expired",
    });
    let theme = localStorage.getItem("theme");
    localStorage.clear();
    if (theme !== null && theme !== undefined) {
      localStorage.setItem("theme", theme);
    }
    window.location.replace(
      isSessionReplaced ? "/?session_replaced=1" : "/?session_expired=1",
    );
    if ("caches" in window) {
      caches.keys().then(function (names) {
        for (let name of names) caches.delete(name);
      });
    }
    window.location.reload(true);
  }, SESSION_EXPIRY_REDIRECT_DELAY);
};

export const getAllNodes = (addedNode) => {
  // loads the config and default system settings from local storage
  let view = getView();
  let facets = view.facets;

  if (addedNode !== undefined && addedNode !== null) {
    facets.push(addedNode);
  }

  let query = AllFacetData;
  if (query === undefined || query === null) {
    query = [];
  }

  if (query !== undefined && query !== null) {
    let facet_response = query;
    if (facet_response === undefined || facet_response === null) {
      return [];
    } else {
      let info = Field_Info;

      let nodes = facets
        .filter(
          (facet) =>
            facet_response[facet] !== null &&
            facet_response[facet] !== undefined,
        )
        .map((facet, index) => {
          // creates the values that will be used by looking into each node and the values set.
          let isActive = view.filters.some(
            (filter) => filter.field_name === facet,
          );

          let facet_response = query[facet];

          let entries = facet_response;

          let children = [];

          if (facet === "path_starts_with") {
            children = getPathNodes(isActive); //TODO need to check this
          } else {
            let sorted_terms = entries.map((bucket) => bucket.val);

            let is_list = false;

            /*
                        Sort based upon the field definition
                    */

            if (
              info.fields[facet] !== undefined &&
              info.fields[facet] !== null
            ) {
              let field_info = info.fields[facet];

              // Gets mandatory fields that are required to be displayed. e.g Terms
              if (field_info.constraints != null) {
                let constraints = info.fields[facet].constraints;
                if (
                  constraints !== undefined &&
                  constraints.list !== undefined &&
                  constraints !== null &&
                  constraints.list !== null
                ) {
                  // need to check
                  sorted_terms = constraints.list;
                  is_list = true;
                }
              }
            }

            // Add in any applied files as per the filer view if it's not listed (shows up with no count)
            if (isActive) {
              view.filters
                .filter((filter) => filter.field_name === facet)
                .forEach((filter) => {
                  filter.value.forEach((val) => {
                    if (sorted_terms.indexOf(val) === -1) {
                      sorted_terms.push(val);
                    }
                  });
                });
            }

            // Tree Facet Query is going to be null
            let facet_query = false;
            if (facet_query === true) {
            } else {
              // Loops through each facet to find the children nodes. E.g finds years for sb_year (2020,2021)
              if (sorted_terms !== null && sorted_terms !== undefined) {
                children = sorted_terms.map((child, childIndex) => {
                  // Gets all the additional info like the count from the inner node
                  let bucket = entries.find((bucket) => bucket.val === child);

                  let count = bucket !== undefined ? bucket.count : 0;

                  let secondaryLabel =
                    bucket !== undefined || is_list ? (
                      <span>{count} </span>
                    ) : undefined;
                  // let icon = "";
                  let isSelected = false;
                  let inverse = false;
                  let filter_rule = undefined;
                  // let isHighlighted = undefined;

                  let filter = undefined;

                  let active_filter_rule = view.filter_rules || [];

                  let rule = Object.entries(view.filter_rules)
                    .filter((val) =>
                      active_filter_rule.some((rule) => rule === val[0]),
                    )
                    .find(
                      (val) =>
                        val[1].field_name === facet &&
                        val[1].value.some((val) => val === child),
                    );

                  if (rule !== undefined && rule !== null) {
                    filter_rule = rule[0];
                    filter = rule[1];
                  }

                  if (filter === undefined || filter === null) {
                    filter = view.filters.find(
                      (filter) =>
                        filter.field_name === facet &&
                        filter.value.some((val) => val === child),
                    );
                  }

                  if (filter !== undefined && filter !== null) {
                    inverse = filter.inverse;
                    isSelected = true;
                  }

                  let label_value = info.lookup[child] || child;

                  let label;

                  //tool tip wording for grid size
                  let tooltipAddToAlbum = <Tooltip>{label_value}</Tooltip>;

                  if (
                    facet === "sb_event" ||
                    facet === "sb_tags" ||
                    facet === "sb_class" ||
                    facet === "sb_student" ||
                    facet === "sb_staff" ||
                    facet === "sb_visitor" ||
                    facet === "sb_pastStudent"
                  ) {
                    label = (
                      <Whisper
                        placement="bottom"
                        trigger="hover"
                        speaker={tooltipAddToAlbum}
                      >
                        <span className="d-flex flex-row wbr200">
                          <span>{label_value}</span>
                          <Tag
                            className="float-right text-xxsmall mt-0 ml-2 pt-0 pb-0"
                            color="blue"
                          >
                            {count}
                          </Tag>
                        </span>
                      </Whisper>
                    );
                  } else {
                    label = (
                      <span className="d-flex flex-row wbr200">
                        <span>{label_value}</span>
                        <Tag
                          className="float-right text-xxsmall mt-0 ml-2 pt-0 pb-0"
                          color="blue"
                        >
                          {count}
                        </Tag>
                      </span>
                    );
                  }

                  // If controlled click or invers a "-" will appear next to it.
                  if (inverse) {
                    if (
                      facet === "sb_event" ||
                      facet === "sb_tags" ||
                      facet === "sb_class" ||
                      facet === "sb_student" ||
                      facet === "sb_staff" ||
                      facet === "sb_visitor" ||
                      facet === "sb_pastStudent"
                    ) {
                      label = (
                        <Whisper
                          placement="bottom"
                          trigger="hover"
                          speaker={tooltipAddToAlbum}
                        >
                          <span className="d-flex flex-row wbr200">
                            <span title={label_value}>- {label_value}</span>
                            <Tag
                              className="float-right text-xxsmall mt-0 ml-2 pt-0 pb-0"
                              color="blue"
                            >
                              {count}
                            </Tag>
                          </span>
                        </Whisper>
                      );
                    } else {
                      label = (
                        <span className="d-flex flex-row wbr200">
                          <span>- {label_value}</span>
                          <Tag
                            className="float-right text-xxsmall mt-0 ml-2 pt-0 pb-0"
                            color="blue"
                          >
                            {count}
                          </Tag>
                        </span>
                      );
                    }
                  }

                  let readableName = info.lookup[facet] || facet;

                  return {
                    id: facet + childIndex,
                    type: "value",
                    inverse,
                    labelExtra: label,
                    label: label_value,
                    filter_rule,
                    className: "",
                    icon: "",
                    secondaryLabel: secondaryLabel,
                    isSelected: isSelected,
                    field_name: facet,
                    value: child,
                    readableName,
                  };
                });
              }
            }
          }

          let isExpanded = false;
          let isSelected = isExpanded === false ? isActive : false;
          // returns a child. with the value and what field_name it belongs too.
          return {
            id: facet,
            hasCaret: true,
            isExpanded: isExpanded,
            isSelected: isSelected,
            label: facet,
            field_name: facet,
            icon: "",
            childNodes: children,
          };
        });

      return nodes;
    }
  } else {
    return [];
  }
};

// Modified getSrc function to handle local files
export const getSrcUpload = (doc) => {
  if (!doc) return "";

  // For local files (temporary preview)
  if (doc.id && doc.id.toString().startsWith("temp-") && doc.preview_url) {
    return doc.preview_url;
  }

  // For server files (regular flow)
  let token = getToken();
  const id = doc.ref || doc.id;
  const kind = determinePreviewKind(doc);

  return `../api/v1/repo/s/semabench/preview?id=${id}&semabench-token=${encodeURIComponent(token)}&kind=${encodeURIComponent(kind)}`;
};

// Helper to determine preview kind
const determinePreviewKind = (doc) => {
  // Default to imgpreview for most cases
  let kind = "imgpreview";

  // Check for other specific kinds
  if (doc.preview_kind && doc.preview_kind.length > 0) {
    // First, check for imgpreview specifically
    if (doc.preview_kind.includes("imgpreview")) {
      return "imgpreview";
    }

    // If no imgpreview, use the first available kind
    return doc.preview_kind[0];
  }

  return kind;
};
